import { useSectionLead } from './sectionLead';
import './Tabs.css';

/**
 * Tabs — barre d'onglets réutilisable (charte), posée dans le corps d'une
 * <PageShell>. Remplace les barres recodées en dur dans chaque hub
 * (.fin-hub__tabs, .people-hub__tabs).
 *
 * Onglet actif souligné en terracotta (charte). En tenue encadrée (parent
 * .page-shell--framed), la barre déborde jusqu'aux bords de l'encadré.
 *
 * Props :
 *   lead     : node             — NOM DE LA SECTION, posé à gauche des onglets et
 *                                 fermé par un filet (2026-08-20). Il remplace le
 *                                 titre H1 de la page : celui-ci répétait l'entrée
 *                                 de menu déjà surlignée dans le panneau, et coûtait
 *                                 un étage entier avant la première donnée. Porté
 *                                 ici, il survit aussi au repliement du panneau.
 *   tabs     : [{ id, label }]  — onglets
 *   active   : string           — id de l'onglet courant
 *   onChange : (id) => void     — sélection d'un onglet
 *   aside    : node             — contrôles de l'écran (vue, unité, filtres…),
 *                                 rendus SUR la ligne des onglets, à droite
 *                                 (retour au format d'origine le 2026-08-20 ;
 *                                 la rangée sous le filet du 2026-08-21 est
 *                                 abandonnée). L'icône télécharger (registre
 *                                 pageExports) ferme la ligne à droite — même
 *                                 place sur toutes les pages.
 *   flow     : bool             — insère une flèche discrète entre les onglets
 *                                 (indique un ordre de construction gauche→droite)
 *   className: string
 *
 * Un onglet peut porter `sep: true` → un séparateur discret est inséré AVANT lui
 * (marque un changement de nature : ex. vues d'analyse | outils de suivi).
 */
export default function Tabs({ tabs, active, onChange, aside, lead, flow = false, className = '' }) {
  /* Le nom de section ne se répète plus sur la ligne d'onglets (2026-09-01) :
     le menu latéral le surligne déjà, « Rapports · Rapports » disait deux fois
     la même chose. `lead` explicite reste honoré pour les rares écrans qui
     posent autre chose qu'un titre. */
  useSectionLead();   // consomme le contexte (le gabarit le fournit toujours)
  const leadNode = lead != null && lead !== '' ? lead : null;
  const sepAt = tabs.findIndex((t, i) => t.sep && i > 0);
  return (
    <nav className={`tabs${className ? ' ' + className : ''}`}>
      {leadNode != null && leadNode !== '' && <span className="tabs__lead">{leadNode}</span>}
      <div className="tabs__list">
        {/* Le séparateur coupe le LIVRABLE de ses SOURCES : ce qui vient après
            n'est plus ce que la section produit, mais ce dont elle se nourrit.
            La distinction existait en trait vertical seulement ; elle porte
            désormais sur le poids du libellé (2026-08-23). */}
        {tabs.flatMap((t, i) => {
          const isSource = sepAt >= 0 && i >= sepAt;
          const btn = (
            <button
              key={t.id}
              type="button"
              className={`tabs__tab${isSource ? ' is-source' : ''}${active === t.id ? ' is-active' : ''}`}
              data-tab={t.id}
              onClick={() => onChange(t.id)}
            >
              {t.label}
            </button>
          );
          if (t.sep && i > 0) {
            return [<span key={`sep-${t.id}`} className="tabs__sep" aria-hidden="true" />, btn];
          }
          return (flow && i > 0)
            ? [<span key={`arrow-${t.id}`} className="tabs__arrow" aria-hidden="true">→</span>, btn]
            : [btn];
        })}
      </div>
      {aside != null && (
        <div className="tabs__aside">
          {aside}
        </div>
      )}
    </nav>
  );
}
